import { useState, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';

interface SidebarContextType {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}

export const SidebarContext = createContext<SidebarContextType>({
  collapsed: false,
  setCollapsed: () => {},
});

export function useSidebar() {
  return useContext(SidebarContext);
}

interface NavItem {
  label: string;
  icon: string;
  to: string;
}

const adminNavItems: NavItem[] = [
  { label: 'Dashboard', icon: 'ri-dashboard-line', to: '/admin' },
  { label: 'Leads', icon: 'ri-user-add-line', to: '/admin/leads' },
  { label: 'Clientes', icon: 'ri-building-line', to: '/admin/clientes' },
  { label: 'Projetos', icon: 'ri-folder-line', to: '/admin/projetos' },
  { label: 'Scrum', icon: 'ri-dashboard-2-line', to: '/admin/scrum' },
  { label: 'Financeiro', icon: 'ri-money-dollar-circle-line', to: '/admin/financeiro' },
  { label: 'Usuários', icon: 'ri-team-line', to: '/admin/usuarios' },
  { label: 'Configurações', icon: 'ri-settings-3-line', to: '/admin/configuracoes' },
];

const userNavItems: NavItem[] = [
  { label: 'Meus Projetos', icon: 'ri-folder-line', to: '/app' },
];

const LOGO_URL = 'https://storage.readdy-site.link/project_files/4a2f3690-6afc-4752-8f11-296a8cd10aa8/9df4885e-fe7a-4292-927f-532659452029_Gemini_Generated_Image_e0eqt8e0eqt8e0eq.png?v=c10a7e5f8467af600c66ebccd92d7add';

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const { profile, signOut } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = profile?.role === 'admin';
  const navItems = isAdmin ? adminNavItems : userNavItems;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isActive = (to: string) => {
    if (to === '/admin' || to === '/app') {
      return location.pathname === to;
    }
    return location.pathname.startsWith(to);
  };

  return (
    <aside
      className={`
        flex flex-col h-screen bg-white border-r border-slate-200 shrink-0
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo + Toggle */}
      <div className={`h-16 flex items-center border-b border-slate-200 shrink-0 ${collapsed ? 'justify-center px-0' : 'px-4 gap-3'}`}>
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2 flex-1 min-w-0">
            <div
              className="shrink-0"
              style={{ borderRadius: '12px', overflow: 'hidden', width: 36, height: 36 }}
            >
              <img
                src={LOGO_URL}
                alt="Granato Tecnologia"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col min-w-0" style={{ lineHeight: 1.1 }}>
              <span className="text-slate-800 font-bold text-sm tracking-tight whitespace-nowrap">Granato</span>
              <span className="text-cyan-500 font-bold text-sm tracking-tight whitespace-nowrap">Tecnologia</span>
            </div>
          </Link>
        )}

        {collapsed && (
          <Link
            to="/"
            className="shrink-0"
            style={{ borderRadius: '12px', overflow: 'hidden', width: 36, height: 36, display: 'block' }}
          >
            <img
              src={LOGO_URL}
              alt="Granato Tecnologia"
              className="w-full h-full object-cover"
            />
          </Link>
        )}

        <button
          onClick={onToggle}
          className={`
            w-7 h-7 flex items-center justify-center rounded-md text-slate-400
            hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer shrink-0
            ${collapsed ? 'absolute left-[52px] top-[18px] bg-white border border-slate-200 shadow-sm z-10' : ''}
          `}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <i className={`text-sm transition-transform duration-300 ${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`}></i>
        </button>
      </div>

      {/* Nav Items */}
      <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
        {navItems.map((item) => {
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              className={`
                flex items-center gap-3 mx-2 mb-0.5 rounded-lg transition-all duration-150 cursor-pointer
                ${collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'}
                ${active
                  ? 'bg-cyan-50 text-cyan-600 border border-cyan-200'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100 border border-transparent'
                }
              `}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-base`}></i>
              </div>
              {!collapsed && (
                <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Info + Logout */}
      <div className={`border-t border-slate-200 py-3 shrink-0 ${collapsed ? 'px-2' : 'px-3'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 mb-1 rounded-lg bg-slate-50 border border-slate-100">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-cyan-100 shrink-0">
              <i className="ri-user-line text-cyan-600 text-xs"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 text-xs font-semibold truncate">
                {profile?.full_name || 'Usuário'}
              </p>
              <p className="text-slate-400 text-xs truncate">{profile?.email}</p>
            </div>
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${isAdmin ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'}`}>
              {isAdmin ? 'Admin' : 'User'}
            </span>
          </div>
        )}

        <button
          onClick={handleSignOut}
          title={collapsed ? 'Sair' : undefined}
          className={`
            w-full flex items-center gap-2 rounded-lg text-slate-500 text-sm
            hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer
            ${collapsed ? 'justify-center py-2.5' : 'px-3 py-2'}
          `}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-logout-box-line text-base"></i>
          </div>
          {!collapsed && <span className="whitespace-nowrap font-medium">Sair</span>}
        </button>
      </div>
    </aside>
  );
}
